import { getConfigBoolean, getConfigValue } from '../utils/env';
import type { AnalyticsConfig, AnalyticsProviderType } from './types';

const PROVIDER_TYPES = new Set<AnalyticsProviderType>(['gtm', 'console']);

const DEFAULT_GTM_CONTAINER_ID = 'GTM-KSZNW4C9';
/** Preferred provider when unset and analytics is off (only used after enable). */
const DEFAULT_PROVIDER: AnalyticsProviderType = 'gtm';

function isProviderType(value: string): value is AnalyticsProviderType {
    return PROVIDER_TYPES.has(value as AnalyticsProviderType);
}

/**
 * Parses a configured provider. Invalid/unknown values return null
 * (never invent GTM). Callers fail closed when enabled + null.
 */
export function parseProvider(
    value: string | undefined
): AnalyticsProviderType | null {
    if (value && isProviderType(value)) {
        return value;
    }
    return null;
}

/**
 * Resolves analytics config from runtime/build-time config.
 * Local/test defaults favor safety (no production GTM traffic).
 *
 * Config keys:
 * - REACT_APP_ANALYTICS_ENABLED
 * - REACT_APP_ANALYTICS_PROVIDER (gtm | console only)
 * - REACT_APP_GTM_CONTAINER_ID (only used when provider is gtm)
 *
 * `noop` is not configurable — used internally when enabled=false.
 * Local dry-run: enabled=true + provider=console
 */
export function resolveAnalyticsConfig(
    overrides: Partial<AnalyticsConfig> = {}
): AnalyticsConfig {
    const isTest =
        typeof process !== 'undefined' && process.env.NODE_ENV === 'test';
    const isDev =
        typeof process !== 'undefined' && process.env.NODE_ENV === 'development';

    const enabledFromConfig = getConfigBoolean(
        'REACT_APP_ANALYTICS_ENABLED',
        false
    );
    const providerFromConfig = parseProvider(
        getConfigValue('REACT_APP_ANALYTICS_PROVIDER', '')
    );
    const gtmContainerIdFromConfig = getConfigValue(
        'REACT_APP_GTM_CONTAINER_ID',
        DEFAULT_GTM_CONTAINER_ID
    );

    let enabled = enabledFromConfig;
    let provider: AnalyticsProviderType =
        providerFromConfig ?? DEFAULT_PROVIDER;

    if (isTest) {
        enabled = false;
        provider = 'console';
    } else if (isDev && !enabledFromConfig) {
        // Local: stay off unless explicitly enabled (use provider=console for dry-run)
        enabled = false;
        provider = providerFromConfig ?? 'console';
    } else if (enabled && providerFromConfig === null) {
        // enabled=true but invalid/missing provider → fail closed
        enabled = false;
        provider = DEFAULT_PROVIDER;
    }

    return {
        enabled: overrides.enabled ?? enabled,
        provider: overrides.provider ?? provider,
        gtmContainerId:
            overrides.gtmContainerId ??
            gtmContainerIdFromConfig ??
            DEFAULT_GTM_CONTAINER_ID,
    };
}
